/**
 * Build the markdown context blob prepended to the operator's first user
 * turn in the "Discuss this Execution" modal.
 *
 * The shape and ordering here is the de-facto contract between the
 * frontend and the backend `automation_debugger` agent: the agent is
 * trained / prompted to look for the `<execution_context>` envelope,
 * scan the `# Execution`, `## Steps`, `## Agent Sessions` sections, and
 * answer follow-up questions citing step indices and session ids.
 *
 * Design notes:
 *
 * - The envelope tags (`<execution_context>` / `</execution_context>`)
 *   give the agent a deterministic delimiter to distinguish the
 *   eager-attached dump from the operator's actual question, which is
 *   appended after the closing tag inside the same user-message text
 *   field. We can't send context as a separate message because the
 *   agent stream endpoint accepts exactly one `new_message` per call
 *   (see `features/session/api.ts:32-77` and ADK wiring).
 *
 * - Per-session transcripts are dumped in full. The TURN_CAP truncation
 *   note is emitted only when `truncated === true` so the agent knows
 *   to ask for a narrower scope if a tail matters.
 *
 * - Step input/output JSON is serialised with `JSON.stringify(_, null, 2)`
 *   for readability. We accept the token cost — the user-confirmed
 *   trade-off is "just give them full context, no fetch-more tool".
 *
 * Pure function; no React, no store reads. Safe to call from tests.
 */

import type { ExecutionDiscussContext, DiscussSessionTranscript } from '../types'
import type { ExecutionStep } from '../../automations/types'
import type { TranscriptTurnItem } from '../../sessions/types'

/** Envelope tag wrapping the entire dump. */
export const DISCUSS_CONTEXT_OPEN = '<execution_context>'
export const DISCUSS_CONTEXT_CLOSE = '</execution_context>'

/**
 * Serialize a full `ExecutionDiscussContext` into the markdown blob that
 * gets prepended to the first user turn. The returned string includes
 * the envelope tags so callers can simply concatenate the operator's
 * message after a separator (see `DiscussMessageInput`).
 */
export function buildDiscussSystemMessage(ctx: ExecutionDiscussContext): string {
  const sections: string[] = []

  sections.push(DISCUSS_CONTEXT_OPEN)
  // Operating-principles preface — defense in depth. The canonical rules
  // live in the agent's SOUL.md, but the envelope reinforces them at the
  // boundary between data and the operator's question so a SOUL drift or
  // misconfigured agent still has the guardrails in hand for this turn.
  sections.push(renderOperatingPrinciples())
  sections.push(renderHeader(ctx))

  const summarySection = renderSummary(ctx.summary)
  if (summarySection) sections.push(summarySection)

  sections.push(renderSteps(ctx.executionSnapshot.steps))

  if (ctx.sessionTranscripts.length > 0) {
    sections.push(renderAgentSessions(ctx.sessionTranscripts))
  }

  sections.push(DISCUSS_CONTEXT_CLOSE)
  return sections.join('\n\n')
}

// ── Section renderers ──────────────────────────────────────────────────────

/**
 * Heading text of the operating-principles preface. Exported so tests
 * can assert the preface is present without coupling to its full body.
 */
export const OPERATING_PRINCIPLES_HEADING =
  '## Operating principles for this conversation'

/**
 * Brief reminder block rendered immediately after the open envelope tag.
 *
 * Mirrors (does not replace) the canonical rules in the
 * `automation_debugger` SOUL.md.  Kept short so the envelope stays
 * readable, but explicit on the two highest-stakes guardrails:
 *
 *   1. Answer only from the data below.  No speculation.
 *   2. When the operator surfaces engineering work, use the eng-tickets
 *      / development-log tools — and dedupe before writing.
 *
 * If the SOUL.md rules drift, update this preface too so a turn-level
 * read of the envelope still gives the agent the right contract.
 */
function renderOperatingPrinciples(): string {
  return [
    OPERATING_PRINCIPLES_HEADING,
    '',
    '- **Facts only.** Answer strictly from the data in this envelope. Cite step indices and session/turn ids when quoting evidence. If the answer is not in here, say so plainly — do not speculate.',
    '- **Flag unverified claims.** When an agent message describes an action that no tool call performed, call it out explicitly. Do not repeat the claim as fact.',
    '- **Respect truncation.** Sessions marked `_(Transcript truncated)_` may have a tail you cannot see. Acknowledge it if the question depends on it.',
    "- **Fetch agent configuration on demand.** This envelope contains transcripts but NOT each agent's own configuration. When the operator asks about what an agent is *configured* to do — its prompt, its declared tools, the policies gating those tools, its model, or any live Firestore overrides — call `get_agent_blueprint(target_agent_id=\"<agent_id>\")`. The returned payload has `composed_instruction` (the live deployed prompt), `tools[]` (every tool with description / domain / `requires_approval` / `blocked` / `read_only`), runtime flags (`has_memory_injection`, `prompt_variables`, `has_gcs_override`), and model + tier labels. Cite the specific field you quote (e.g. `tools[name=issue_refund].blocked`). Do not call this reflexively — only when configuration is the subject.",
    '- **File engineering work properly.** When the operator surfaces a bug, feature request, prompt issue, or missing capability, use `report_eng_ticket` (code/platform/customer-facing) or `create_development_log_entry` (agent prompt / capability / inaccuracy). ALWAYS call `get_eng_tickets` / `list_development_log` first to dedupe, and confirm the draft with the operator before writing.',
    '- **Read-only on the execution.** You cannot retry steps, send communications, or modify customer data — only answer questions, fetch prompts when needed, and file engineering entries.',
  ].join('\n')
}

function renderHeader(ctx: ExecutionDiscussContext): string {
  const lines: string[] = ['# Execution']
  lines.push(`- ID: \`${ctx.executionId}\``)
  if (ctx.automationId) lines.push(`- Automation: \`${ctx.automationId}\``)
  lines.push(`- Status: ${ctx.executionSnapshot.status}`)
  if (ctx.executionSnapshot.startedAt) {
    lines.push(`- Started: ${ctx.executionSnapshot.startedAt}`)
  }
  if (ctx.executionSnapshot.completedAt) {
    lines.push(`- Completed: ${ctx.executionSnapshot.completedAt}`)
  }
  if (typeof ctx.executionSnapshot.totalCost === 'number') {
    lines.push(`- Total cost: $${ctx.executionSnapshot.totalCost.toFixed(4)}`)
  }
  if (ctx.attentionItemId) {
    lines.push(`- Attention item: \`${ctx.attentionItemId}\``)
  }

  const params = ctx.executionSnapshot.parameters
  if (params && Object.keys(params).length > 0) {
    lines.push('')
    lines.push('## Parameters')
    lines.push('```json')
    lines.push(safeStringify(params))
    lines.push('```')
  }

  return lines.join('\n')
}

function renderSummary(summary: Record<string, unknown> | undefined): string | null {
  if (!summary || Object.keys(summary).length === 0) return null
  return [
    '## Summary',
    '_Structured summary from `/v1/executions/{id}/summarise`. Use as a starting point; cross-reference with the steps below for evidence._',
    '',
    '```json',
    safeStringify(summary),
    '```',
  ].join('\n')
}

function renderSteps(steps: ExecutionStep[]): string {
  const lines: string[] = ['## Steps']
  if (steps.length === 0) {
    lines.push('_No steps recorded for this execution._')
    return lines.join('\n')
  }

  for (const step of steps) {
    lines.push(`### Step ${step.index}: ${step.name}`)
    const meta: string[] = [`status: ${step.status}`]
    if (typeof step.durationMs === 'number') meta.push(`duration: ${step.durationMs}ms`)
    if (typeof step.cost === 'number') meta.push(`cost: $${step.cost.toFixed(4)}`)
    if (step.startedAt) meta.push(`started: ${step.startedAt}`)
    if (step.completedAt) meta.push(`completed: ${step.completedAt}`)
    lines.push(`- ${meta.join(' · ')}`)

    if (step.error) {
      lines.push('')
      lines.push('**Error:**')
      lines.push('```')
      lines.push(step.error.message)
      if (step.error.stack) {
        lines.push('')
        lines.push(step.error.stack)
      }
      lines.push('```')
    }

    if (step.input !== undefined && step.input !== null) {
      lines.push('')
      lines.push('**Input:**')
      lines.push('```json')
      lines.push(safeStringify(step.input))
      lines.push('```')
    }
    if (step.output !== undefined && step.output !== null) {
      lines.push('')
      lines.push('**Output:**')
      lines.push('```json')
      lines.push(safeStringify(step.output))
      lines.push('```')
    }
    lines.push('')
  }

  return lines.join('\n').trimEnd()
}

function renderAgentSessions(transcripts: DiscussSessionTranscript[]): string {
  const lines: string[] = ['## Agent Sessions']
  lines.push(
    `_${transcripts.length} agent session${transcripts.length === 1 ? '' : 's'} spawned by \`invoke_agent\` steps. Quote the session id (e.g. \`${transcripts[0]?.sessionId ?? 'ses_…'}\`) when citing evidence._`,
  )
  lines.push('')

  for (const t of transcripts) {
    const heading = `### Session \`${t.sessionId}\` — ${t.agentName ?? t.agentId ?? 'unknown agent'}  (Step ${t.stepIndex}: ${t.stepName})`
    lines.push(heading)

    if (t.error) {
      lines.push(`_Transcript failed to load: ${t.error}_`)
      lines.push('')
      continue
    }

    if (t.turns.length === 0) {
      lines.push('_No turns recorded for this session._')
      lines.push('')
      continue
    }

    for (const turn of t.turns) {
      lines.push(renderTurn(turn))
    }

    if (t.truncated) {
      lines.push('')
      lines.push(
        `_(Transcript truncated at ${t.turns.length} turns. More events exist on the backend — ask the operator to scope the question to a narrower step if the missing tail matters.)_`,
      )
    }
    lines.push('')
  }

  return lines.join('\n').trimEnd()
}

function renderTurn(turn: TranscriptTurnItem): string {
  const lines: string[] = []
  const author = turn.author ? ` (${turn.author})` : ''
  const tool = turn.toolName ? ` ${turn.toolName}` : ''
  lines.push(`#### [Turn ${turn.turnIndex}] ${turn.role}${author}${tool}`)

  if (turn.thinkingText && turn.thinkingText.trim().length > 0) {
    lines.push('_thinking:_ ' + indentBlock(turn.thinkingText.trim()))
  }

  const content = renderTurnContent(turn.content)
  if (content) lines.push(content)

  if (turn.transferredToAgent) {
    lines.push(`_(transferred to agent: \`${turn.transferredToAgent}\`)_`)
  }
  if (turn.targetAgent) {
    lines.push(`_(a2a call → \`${turn.targetAgent}\`)_`)
  }
  return lines.join('\n')
}

function renderTurnContent(content: TranscriptTurnItem['content']): string {
  if (typeof content === 'string') {
    return content.trim().length > 0 ? content : '_(empty)_'
  }
  if (content && typeof content === 'object') {
    return ['```json', safeStringify(content), '```'].join('\n')
  }
  return '_(no content)_'
}

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * Stringify untrusted JSON without throwing on circular references or
 * BigInt values. Both are unlikely in execution payloads but we never
 * want context-building to crash the modal.
 */
function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(
      value,
      (_key, v) => (typeof v === 'bigint' ? v.toString() : v),
      2,
    )
  } catch {
    try {
      return String(value)
    } catch {
      return '[unserialisable value]'
    }
  }
}

/** Indent every line by two spaces — used inside markdown blockquotes
 *  so multi-line thinking text doesn't break list rendering. */
function indentBlock(text: string): string {
  return text
    .split('\n')
    .map((line) => '  ' + line)
    .join('\n')
}
